/**
 * Pieces measured off the Figma frame (Landing. template, 296:3236) that the
 * design system has no equivalent for. Everything here carries the node id it
 * came from and the raw Figma value, so a later re-measure is a diff, not an
 * archaeology exercise.
 *
 * FIGMA BLUR RADII ARE DOUBLE THE CSS ONES. A Figma blur of 20.6 is
 * `blur(10.3px)` in CSS — the same halving Figma's own Dev Mode does when it
 * emits Tailwind. Every radius below is already converted.
 */

/**
 * Progressive blur, the layered-mask technique.
 *
 * CSS has no progressive blur, so N layers each add a little blur and each
 * unmasks slightly further along the axis. Because a `backdrop-filter` layer
 * sees the output of the layer beneath it, the blurs compound in quadrature —
 * the per-layer radii below are chosen so the CUMULATIVE radius climbs in
 * equal steps to `radius` by the far end.
 *
 * `from`/`to` are percentages along `direction`, measured the way the CSS
 * gradient runs: `from` is where the blur starts (still sharp) and `to` is
 * where it reaches full strength.
 */
export function progressiveBlurLayers({
  radius,
  from,
  to,
  direction,
  steps = 5,
}: {
  radius: number
  from: number
  to: number
  direction: "to left" | "to right" | "to top" | "to bottom"
  steps?: number
}) {
  const span = (to - from) / steps
  let previous = 0
  return Array.from({ length: steps }, (_, i) => {
    const cumulative = (radius * (i + 1)) / steps
    // Quadrature: stacking blur(a) then blur(b) reads as blur(√(a²+b²)).
    const layer = Math.sqrt(cumulative * cumulative - previous * previous)
    previous = cumulative
    const start = from + span * i
    const end = from + span * (i + 1)
    const mask = `linear-gradient(${direction}, transparent ${start}%, black ${end}%)`
    return {
      backdropFilter: `blur(${layer.toFixed(2)}px)`,
      WebkitBackdropFilter: `blur(${layer.toFixed(2)}px)`,
      maskImage: mask,
      WebkitMaskImage: mask,
    }
  })
}

/** Same ramp, for a LAYER blur on an element rather than on its backdrop. */
export function progressiveLayerMasks({
  radius,
  from,
  to,
  direction,
  steps = 4,
}: {
  radius: number
  from: number
  to: number
  direction: "to left" | "to right" | "to top" | "to bottom"
  steps?: number
}) {
  const span = (to - from) / steps
  return Array.from({ length: steps }, (_, i) => {
    const start = from + span * i
    const end = from + span * (i + 1)
    // Each copy is masked to its own band and blurred to that band's strength,
    // so the sharp original shows through everywhere before `from`.
    const mask =
      i === steps - 1
        ? `linear-gradient(${direction}, transparent ${start}%, black ${end}%)`
        : `linear-gradient(${direction}, transparent ${start}%, black ${end}%, black ${end + span}%, transparent ${end + span}%)`
    return {
      filter: `blur(${((radius * (i + 1)) / steps).toFixed(2)}px)`,
      maskImage: mask,
      WebkitMaskImage: mask,
    }
  })
}

/**
 * Section 01's list mark. The Figma (296:3398) draws a check inside a 34px
 * chip, and a check reads as "all of this is required" — Michèle on AHD-11317,
 * 16.09: these are three reasons to start, not a checklist. Same 18px box and
 * same 2px round stroke as that check, so the rhythm of the list is unchanged;
 * only the glyph and the chip behind it are gone.
 */
export function FigmaDashIcon({ className }: { className?: string }) {
  return (
    <svg
      width={18}
      height={18}
      viewBox="0 0 18 18"
      fill="none"
      className={className}
      aria-hidden
    >
      <path
        d="M2 9H16"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
      />
    </svg>
  )
}

/** Figma 336:3443 — Material Symbols, 28px, exported from the design file. */
export function VpnKeyIcon({ className }: { className?: string }) {
  return (
    <svg
      width={28}
      height={28}
      viewBox="0 0 28 28"
      fill="none"
      className={className}
      aria-hidden
    >
      <path
        d="M8.16797 21C6.22352 21 4.57075 20.3194 3.20964 18.9583C1.84852 17.5972 1.16797 15.9444 1.16797 14C1.16797 12.0556 1.84852 10.4028 3.20964 9.04167C4.57075 7.68056 6.22352 7 8.16797 7C9.60686 7 10.8513 7.35 11.9013 8.05C12.9513 8.75 13.7485 9.67682 14.293 10.8305H26.8346V17.1695H23.9958V21H18.0458V17.1695H14.293C13.7485 18.3232 12.9513 19.25 11.9013 19.95C10.8513 20.65 9.60686 21 8.16797 21ZM8.16797 19.0555C9.51605 19.0555 10.6147 18.6196 11.4638 17.7479C12.3129 16.8762 12.8153 16.0352 12.9708 15.225H20.068V19.0555H22.0513V15.225H24.8901V12.775H12.9708C12.8153 11.9647 12.3129 11.1238 11.4638 10.2521C10.6147 9.38039 9.51605 8.94454 8.16797 8.94454C6.76797 8.94454 5.57534 9.43707 4.59009 10.4221C3.60504 11.4074 3.11251 12.6 3.11251 14C3.11251 15.4 3.60504 16.5926 4.59009 17.5779C5.57534 18.5629 6.76797 19.0555 8.16797 19.0555ZM8.16797 16.1C8.75772 16.1 9.25521 15.8975 9.66043 15.4925C10.0655 15.0872 10.268 14.5897 10.268 14C10.268 13.4102 10.0655 12.9128 9.66043 12.5075C9.25521 12.1025 8.75772 11.9 8.16797 11.9C7.57822 11.9 7.08073 12.1025 6.67551 12.5075C6.27048 12.9128 6.06797 13.4102 6.06797 14C6.06797 14.5897 6.27048 15.0872 6.67551 15.4925C7.08073 15.8975 7.57822 16.1 8.16797 16.1Z"
        fill="currentColor"
      />
    </svg>
  )
}

/** Figma 336:3432 — Material Symbols, 28px, exported from the design file. */
export function HealthAndSafetyIcon({ className }: { className?: string }) {
  return (
    <svg
      width={28}
      height={28}
      viewBox="0 0 28 28"
      fill="none"
      className={className}
      aria-hidden
    >
      <path
        d="M12.6401 17.6942H15.3625V14.7776H18.2792V12.0551H15.3625V9.13848H12.6401V12.0551H9.72343V14.7776H12.6401V17.6942ZM14.0013 25.6468C11.2855 24.9663 9.05104 23.3961 7.29793 20.9364C5.54462 18.4767 4.66797 15.769 4.66797 12.8135V5.81348L14.0013 2.31348L23.3346 5.81348V12.8135C23.3346 15.769 22.458 18.4767 20.7047 20.9364C18.9516 23.3961 16.7171 24.9663 14.0013 25.6468ZM14.0013 23.6247C16.1661 22.9183 17.9387 21.5604 19.3193 19.551C20.6998 17.5418 21.3901 15.296 21.3901 12.8135V7.16506L14.0013 4.38431L6.61251 7.16506V12.8135C6.61251 15.296 7.30279 17.5418 8.68334 19.551C10.0639 21.5604 11.8366 22.9183 14.0013 23.6247Z"
        fill="currentColor"
      />
    </svg>
  )
}

/** Figma 336:3439 — Material Symbols, 28px, exported from the design file. */
export function LockPersonIcon({ className }: { className?: string }) {
  return (
    <svg
      width={28}
      height={28}
      viewBox="0 0 28 28"
      fill="none"
      className={className}
      aria-hidden
    >
      <path
        d="M22.9785 20.275C23.3456 19.9078 23.5292 19.4622 23.5292 18.938C23.5292 18.4137 23.3458 17.9718 22.9791 17.612C22.6124 17.2523 22.167 17.0724 21.643 17.0724C21.1244 17.0724 20.6837 17.2546 20.3208 17.619C19.9578 17.9832 19.7763 18.4256 19.7763 18.9461C19.7763 19.4664 19.9562 19.9098 20.3159 20.2761C20.6756 20.6423 21.1176 20.8253 21.6418 20.8253C22.166 20.8253 22.6116 20.6419 22.9785 20.275ZM23.393 24.1407C23.9374 23.849 24.3814 23.4471 24.725 22.9349C24.2389 22.6627 23.7398 22.4505 23.2276 22.2982C22.7156 22.146 22.1874 22.0699 21.643 22.0699C21.0985 22.0699 20.5703 22.146 20.0583 22.2982C19.5462 22.4505 19.047 22.6627 18.5609 22.9349C18.9045 23.4471 19.3472 23.849 19.8889 24.1407C20.4304 24.4324 21.0137 24.5782 21.6389 24.5782C22.2638 24.5782 22.8485 24.4324 23.393 24.1407ZM10.3068 9.45033H17.6958V6.86412C17.6958 5.80128 17.3393 4.91014 16.6263 4.1907C15.9133 3.47126 15.0383 3.11153 14.0013 3.11153C12.9643 3.11153 12.0893 3.47126 11.3763 4.1907C10.6633 4.91014 10.3068 5.80128 10.3068 6.86412V9.45033ZM15.5958 25.667H6.61251C6.10034 25.667 5.64826 25.4709 5.25626 25.0787C4.86407 24.6867 4.66797 24.2346 4.66797 23.7225V11.3949C4.66797 10.8827 4.86407 10.4306 5.25626 10.0386C5.64826 9.64642 6.10034 9.45033 6.61251 9.45033H8.36251V6.86412C8.36251 5.27628 8.90851 3.92976 10.0005 2.82453C11.0927 1.71951 12.4263 1.16699 14.0013 1.16699C15.5763 1.16699 16.9099 1.71951 18.0021 2.82453C19.0941 3.92976 19.6401 5.27628 19.6401 6.86412V9.45033H21.3901C21.9023 9.45033 22.3543 9.64642 22.7463 10.0386C23.1385 10.4306 23.3346 10.8827 23.3346 11.3949V13.592C23.0494 13.5142 22.7529 13.4591 22.4451 13.4266C22.1372 13.3943 21.7856 13.3782 21.3901 13.3782V11.3949H6.61251V23.7225H14.5263C14.6688 24.0985 14.8179 24.4307 14.9734 24.7191C15.129 25.0074 15.3365 25.3234 15.5958 25.667ZM17.6229 25.0399C16.5049 23.9219 15.9458 22.5818 15.9458 21.0199C15.9458 19.4577 16.5049 18.1176 17.6229 16.9995C18.741 15.8815 20.081 15.3225 21.643 15.3225C23.2049 15.3225 24.545 15.8815 25.663 16.9995C26.7811 18.1176 27.3401 19.4577 27.3401 21.0199C27.3401 22.5818 26.7811 23.9219 25.663 25.0399C24.545 26.158 23.2049 26.717 21.643 26.717C20.081 26.717 18.741 26.158 17.6229 25.0399Z"
        fill="currentColor"
      />
    </svg>
  )
}

/** Figma 314:11693 / 314:11699 — the baseline card's promise icons, 28px. */
export function ShieldPlusIcon({ className }: { className?: string }) {
  return (
    <svg
      width={28}
      height={28}
      viewBox="0 0 28 28"
      fill="none"
      className={className}
      aria-hidden
    >
      <path
        d="M13.9997 26.338C16.7079 25.477 18.9479 23.7541 20.7189 21.1711C22.489 18.5881 23.3745 15.7181 23.3745 12.5603V5.16302L13.9997 1.6709L4.625 5.16302V12.5603C4.625 15.7181 5.5105 18.5881 7.28062 21.1711C9.05162 23.7541 11.2916 25.477 13.9997 26.338Z"
        stroke="currentColor"
        strokeWidth="1.3125"
      />
      <path
        d="M12.179 18.9583H15.8242V14.9473H19.8352V11.302H15.8242V7.29102H12.179V11.302H8.16797V14.9473H12.179V18.9583Z"
        fill="currentColor"
      />
    </svg>
  )
}

/** Figma 314:11693 / 314:11699 — the baseline card's promise icons, 28px. */
export function TargetIcon({ className }: { className?: string }) {
  return (
    <svg
      width={28}
      height={28}
      viewBox="0 0 28 28"
      fill="none"
      className={className}
      aria-hidden
    >
      <path
        d="M14.0013 25.0837C20.1225 25.0837 25.0846 20.1215 25.0846 14.0003C25.0846 7.87917 20.1225 2.91699 14.0013 2.91699C7.88015 2.91699 2.91797 7.87917 2.91797 14.0003C2.91797 20.1215 7.88015 25.0837 14.0013 25.0837Z"
        stroke="currentColor"
        strokeWidth="1.16667"
      />
      <path
        d="M14 21.749C18.2802 21.749 21.75 18.2792 21.75 13.999C21.75 9.71882 18.2802 6.24902 14 6.24902C9.71979 6.24902 6.25 9.71882 6.25 13.999C6.25 18.2792 9.71979 21.749 14 21.749Z"
        stroke="currentColor"
        strokeWidth="1.16667"
      />
      <path
        d="M13.9987 18.4183C16.4379 18.4183 18.4154 16.4409 18.4154 14.0016C18.4154 11.5624 16.4379 9.58496 13.9987 9.58496C11.5594 9.58496 9.58203 11.5624 9.58203 14.0016C9.58203 16.4409 11.5594 18.4183 13.9987 18.4183Z"
        stroke="currentColor"
        strokeWidth="1.16667"
      />
    </svg>
  )
}

/**
 * Figma 314:11706 — the baseline card's stethoscope, exported from the file.
 * The design system's own `StethoscopeIcon` draws a different, badly
 * proportioned shape at this size; this is the mark the design actually uses.
 * viewBox is the export's own 27.07 square, not 28, so the strokes land where
 * they were drawn.
 */
export function StethoscopeFigmaIcon({ className }: { className?: string }) {
  return (
    <svg
      width={28}
      height={28}
      viewBox="0 0 27.0658 27.0669"
      fill="none"
      className={className}
      aria-hidden
    >
      <path
        d="M14.9852 0.7V2.83003"
        stroke="currentColor"
        strokeWidth={1.4}
        strokeMiterlimit={10}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M22.4422 0.7V2.83003"
        stroke="currentColor"
        strokeWidth={1.4}
        strokeMiterlimit={10}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M16.2039 16.0848L18.7105 22.0007L21.2171 16.0848"
        stroke="currentColor"
        strokeWidth={1.4}
        strokeMiterlimit={10}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M22.4382 1.76543H23.701C25.4701 1.76543 26.7473 3.45854 26.2613 5.15943L24.1411 12.5795C23.4483 15.004 21.2321 16.6756 18.7104 16.6756C16.1886 16.6756 13.9724 15.004 13.2797 12.5795L11.1595 5.15943C10.6735 3.45854 11.9508 1.76543 13.7199 1.76543H14.9827"
        stroke="currentColor"
        strokeWidth={1.4}
        strokeMiterlimit={10}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3.89531 19.8703V17.2611C3.89531 15.173 5.58815 13.4803 7.67634 13.4803C9.76453 13.4803 11.4573 15.173 11.4573 17.2611V22.7391C11.4573 24.7427 13.0817 26.3669 15.0854 26.3669C17.0892 26.3669 18.7135 24.7427 18.7135 22.7391V22.0003"
        stroke="currentColor"
        strokeWidth={1.4}
        strokeMiterlimit={10}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M0.7 23.0648C0.7 24.8294 2.13055 26.2599 3.89523 26.2599C5.65991 26.2599 7.09047 24.8294 7.09047 23.0648C7.09047 21.3002 5.65991 19.8698 3.89523 19.8698C2.13055 19.8698 0.7 21.3002 0.7 23.0648Z"
        stroke="currentColor"
        strokeWidth={1.4}
        strokeMiterlimit={10}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3.09453 23.0651C3.09453 23.5063 3.45218 23.8639 3.89334 23.8639C4.3345 23.8639 4.69215 23.5063 4.69215 23.0651C4.69215 22.624 4.3345 22.2664 3.89334 22.2664C3.45218 22.2664 3.09453 22.624 3.09453 23.0651Z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth={1.4}
        strokeMiterlimit={10}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * Figma 314:11347 — the seal on the member-pricing row. Not a check: the row
 * says a discount applies, and the design marks it differently from the five
 * plain inclusions. The seal takes `currentColor` (deep-green-500 in the file,
 * #408C49) with a white percent mark.
 */
export function PercentSealIcon({ className }: { className?: string }) {
  return (
    <svg
      width={20}
      height={20}
      viewBox="0 0 20 20"
      fill="none"
      className={className}
      aria-hidden
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.0002 20.0001C9.62024 20.0001 9.25708 19.9286 8.91287 19.7865C8.5694 19.6441 8.25773 19.4348 7.99603 19.1707C7.46761 18.6475 7.01287 18.3065 6.63497 18.1454C6.25603 17.9833 5.68866 17.9033 4.93182 17.9033C4.14445 17.9033 3.47498 17.6275 2.92445 17.077C2.65677 16.8164 2.44527 16.5038 2.30298 16.1584C2.16069 15.813 2.09062 15.4421 2.09708 15.0686C2.09708 14.3191 2.01603 13.7528 1.85498 13.3696C1.69287 12.9875 1.35182 12.5317 0.829712 12.0044C0.564856 11.7428 0.354797 11.4312 0.211817 11.0875C0.0673264 10.7442 -0.00470421 10.3747 0.000238158 10.0023C0.000238158 9.62646 0.0707645 9.26646 0.211817 8.9212C0.35287 8.57593 0.559186 8.26751 0.829712 7.99593C1.35182 7.47172 1.69287 7.02014 1.85498 6.64014C2.01603 6.26014 2.09708 5.69067 2.09708 4.93172C2.09708 4.14436 2.37287 3.47488 2.92445 2.9233C3.185 2.65594 3.49749 2.44472 3.8427 2.30261C4.18792 2.16051 4.55855 2.09053 4.93182 2.09699C5.68129 2.09699 6.24761 2.01593 6.63076 1.85488C7.01287 1.69278 7.46866 1.35172 7.99603 0.829618C8.2634 0.561197 8.56866 0.355934 8.91392 0.213829C9.25612 0.0690428 9.62448 -0.00369693 9.99603 0.000144598C10.3729 0.000144598 10.7339 0.0706709 11.0792 0.211724C11.4244 0.352776 11.7329 0.559092 12.0044 0.829618C12.5287 1.35172 12.9802 1.69278 13.3602 1.85488C13.7402 2.01593 14.3097 2.09699 15.0687 2.09699C15.856 2.09699 16.5255 2.37278 17.0771 2.9233C17.6276 3.47488 17.9034 4.14436 17.9034 4.93172C17.9034 5.6812 17.9845 6.24751 18.1455 6.63067C18.3076 7.01278 18.6487 7.46857 19.1708 7.99593C19.4413 8.26751 19.6476 8.57488 19.7887 8.91909C19.9297 9.2633 20.0002 9.6233 20.0002 9.99804C20.0002 10.3738 19.9297 10.7338 19.7887 11.0791C19.6467 11.4257 19.4366 11.7404 19.1708 12.0044C18.6476 12.5328 18.3066 12.9875 18.1455 13.3654C17.9834 13.7444 17.9034 14.3117 17.9034 15.0686C17.9034 15.8559 17.6276 16.5254 17.0771 17.077C16.8164 17.3445 16.5037 17.5558 16.1583 17.6979C15.8129 17.84 15.4421 17.9099 15.0687 17.9033C14.3192 17.9033 13.7529 17.9844 13.3697 18.1454C12.9876 18.3075 12.5318 18.6486 12.0044 19.1707C11.7428 19.4348 11.4311 19.6441 11.0876 19.7865C10.7438 19.932 10.3736 20.0047 10.0002 20.0001ZM12.5634 13.857C12.9266 13.857 13.2329 13.7317 13.4823 13.4822C13.7318 13.2328 13.856 12.9265 13.856 12.5633C13.856 12.1991 13.7318 11.8907 13.4823 11.6359C13.2329 11.3823 12.9266 11.2549 12.5634 11.2549C12.1992 11.2549 11.8908 11.3823 11.636 11.6359C11.3823 11.8907 11.255 12.1991 11.255 12.5633C11.255 12.9265 11.3823 13.2328 11.636 13.4822C11.8908 13.7317 12.1992 13.857 12.5634 13.857ZM7.38655 13.7128L13.7129 7.39699L12.6034 6.28751L6.28761 12.6138L7.38655 13.7128ZM8.36445 8.36436C8.61813 8.10962 8.7455 7.8012 8.7455 7.43699C8.7455 7.07383 8.61813 6.76751 8.36445 6.51804C8.11498 6.26857 7.80866 6.14436 7.43708 6.14436C7.07392 6.14436 6.76761 6.26857 6.51813 6.51804C6.26866 6.76751 6.1434 7.07383 6.1434 7.43699C6.1434 7.8012 6.26866 8.10962 6.51813 8.36436C6.76761 8.61383 7.07392 8.7412 7.43708 8.7412C7.80866 8.7412 8.11498 8.61383 8.36445 8.36436Z"
        fill="currentColor"
      />
      {/* The % itself, filled rather than cut out: the seal is one
          even-odd path, so on the dark photograph its counters read as
          holes instead of a percent sign (Eugene, 16.09). Same white as
          the DS `CheckCircleNeutralIcon` tick it sits in a row with. */}
      <path
        d="M12.5634 13.857C12.9266 13.857 13.2329 13.7317 13.4823 13.4822C13.7318 13.2328 13.856 12.9265 13.856 12.5633C13.856 12.1991 13.7318 11.8907 13.4823 11.6359C13.2329 11.3823 12.9266 11.2549 12.5634 11.2549C12.1992 11.2549 11.8908 11.3823 11.636 11.6359C11.3823 11.8907 11.255 12.1991 11.255 12.5633C11.255 12.9265 11.3823 13.2328 11.636 13.4822C11.8908 13.7317 12.1992 13.857 12.5634 13.857ZM7.38655 13.7128L13.7129 7.39699L12.6034 6.28751L6.28761 12.6138L7.38655 13.7128ZM8.36445 8.36436C8.61813 8.10962 8.7455 7.8012 8.7455 7.43699C8.7455 7.07383 8.61813 6.76751 8.36445 6.51804C8.11498 6.26857 7.80866 6.14436 7.43708 6.14436C7.07392 6.14436 6.76761 6.26857 6.51813 6.51804C6.26866 6.76751 6.1434 7.07383 6.1434 7.43699C6.1434 7.8012 6.26866 8.10962 6.51813 8.36436C6.76761 8.61383 7.07392 8.7412 7.43708 8.7412C7.80866 8.7412 8.11498 8.61383 8.36445 8.36436Z"
        fill="#fff"
      />
    </svg>
  )
}

/** Figma 336:3549 — CalendarCheck from the closing band, 24×23.5. */
export function CalendarCheckIcon({ className }: { className?: string }) {
  return (
    <svg
      width={24}
      height={24}
      viewBox="0 0 24 23.5"
      fill="none"
      className={className}
      aria-hidden
    >
      <path
        d="M19.5 3.5H17.25V2.75C17.25 2.55109 17.171 2.36032 17.0303 2.21967C16.8897 2.07902 16.6989 2 16.5 2C16.3011 2 16.1103 2.07902 15.9697 2.21967C15.829 2.36032 15.75 2.55109 15.75 2.75V3.5H8.25V2.75C8.25 2.55109 8.17098 2.36032 8.03033 2.21967C7.88968 2.07902 7.69891 2 7.5 2C7.30109 2 7.11032 2.07902 6.96967 2.21967C6.82902 2.36032 6.75 2.55109 6.75 2.75V3.5H4.5C4.10218 3.5 3.72064 3.65804 3.43934 3.93934C3.15804 4.22064 3 4.60218 3 5V20C3 20.3978 3.15804 20.7794 3.43934 21.0607C3.72064 21.342 4.10218 21.5 4.5 21.5H19.5C19.8978 21.5 20.2794 21.342 20.5607 21.0607C20.842 20.7794 21 20.3978 21 20V5C21 4.60218 20.842 4.22064 20.5607 3.93934C20.2794 3.65804 19.8978 3.5 19.5 3.5ZM6.75 5V5.75C6.75 5.94891 6.82902 6.13968 6.96967 6.28033C7.11032 6.42098 7.30109 6.5 7.5 6.5C7.69891 6.5 7.88968 6.42098 8.03033 6.28033C8.17098 6.13968 8.25 5.94891 8.25 5.75V5H15.75V5.75C15.75 5.94891 15.829 6.13968 15.9697 6.28033C16.1103 6.42098 16.3011 6.5 16.5 6.5C16.6989 6.5 16.8897 6.42098 17.0303 6.28033C17.171 6.13968 17.25 5.94891 17.25 5.75V5H19.5V8H4.5V5H6.75ZM19.5 20H4.5V9.5H19.5V20ZM15.9056 11.9694C15.9754 12.039 16.0307 12.1217 16.0684 12.2128C16.1062 12.3038 16.1256 12.4014 16.1256 12.5C16.1256 12.5986 16.1062 12.6962 16.0684 12.7872C16.0307 12.8783 15.9754 12.961 15.9056 13.0306L11.4056 17.5306C11.336 17.6004 11.2533 17.6557 11.1622 17.6934C11.0712 17.7312 10.9736 17.7506 10.875 17.7506C10.7764 17.7506 10.6788 17.7312 10.5878 17.6934C10.4967 17.6557 10.414 17.6004 10.3444 17.5306L8.09437 15.2806C7.95364 15.1399 7.87458 14.949 7.87458 14.75C7.87458 14.551 7.95364 14.3601 8.09437 14.2194C8.23511 14.0786 8.42598 13.9996 8.625 13.9996C8.82402 13.9996 9.01489 14.0786 9.15563 14.2194L10.875 15.9397L14.8444 11.9694C14.914 11.8996 14.9967 11.8443 15.0878 11.8066C15.1788 11.7688 15.2764 11.7494 15.375 11.7494C15.4736 11.7494 15.5712 11.7688 15.6622 11.8066C15.7533 11.8443 15.836 11.8996 15.9056 11.9694Z"
        fill="currentColor"
      />
    </svg>
  )
}

/** Figma 336:3533 — the closing band's plain check, 17px, no circle. The
 *  DS `CheckIcon` is a filled olive disc with a white tick, a different mark. */
export function CheckPlainIcon({ className }: { className?: string }) {
  return (
    <svg
      width={17}
      height={17}
      viewBox="0 0 17 17"
      fill="none"
      className={className}
      aria-hidden
    >
      <path
        d="M15.4369 5.34478L6.93693 13.8448C6.8629 13.9191 6.77493 13.978 6.67807 14.0182C6.5812 14.0585 6.47736 14.0792 6.37248 14.0792C6.2676 14.0792 6.16375 14.0585 6.06689 14.0182C5.97002 13.978 5.88206 13.9191 5.80802 13.8448L2.08927 10.126C2.01515 10.0519 1.95635 9.96391 1.91623 9.86706C1.87612 9.77021 1.85547 9.66641 1.85547 9.56158C1.85547 9.45675 1.87612 9.35295 1.91623 9.2561C1.95635 9.15925 2.01515 9.07125 2.08927 8.99713C2.1634 8.923 2.2514 8.8642 2.34825 8.82408C2.4451 8.78397 2.5489 8.76332 2.65373 8.76332C2.75855 8.76332 2.86236 8.78397 2.95921 8.82408C3.05605 8.8642 3.14405 8.923 3.21818 8.99713L6.37314 12.1521L14.3094 4.2172C14.4591 4.0675 14.6621 3.9834 14.8738 3.9834C15.0855 3.9834 15.2886 4.0675 15.4383 4.2172C15.588 4.3669 15.6721 4.56994 15.6721 4.78166C15.6721 4.99337 15.588 5.19641 15.4383 5.34611L15.4369 5.34478Z"
        fill="currentColor"
      />
    </svg>
  )
}
